import React, { useEffect, useState } from 'react';

import {
  formatDateToShortDotV2,
  getRemainingTime,
  BaseButton,
  useAuth,
  useLoginRequiredModal,
  useT,
} from '@repo/common';

import ApplyNowModal from '../Apply/ApplyNowModal';

import { ReactComponent as BookmarkOutlineIcon } from '@repo/common/assets/icons/32px/icon_32px_bookmark_outline.svg';
import { ReactComponent as BookmarkFilledIcon } from '@repo/common/assets/icons/32px/icon_32px_bookmark_filled.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';

/**
 * 모바일 전용 하단 고정 지원 바
 */
const MobileApplyBar = ({
  job,
  isSaved,
  isHidden,
  hasApplied,
  onApplied,
  onToggleSave,
}) => {
  const t = useT();
  const { isLoggedIn } = useAuth();
  const { showLoginModal } = useLoginRequiredModal();

  const { applicationDeadline, hiringStatus } = job || {};
  const isOngoing = hiringStatus === 'ONGOING_HIRING';

  const [remainingTime, setRemainingTime] = useState('');
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isPillDismissed, setIsPillDismissed] = useState(false);

  useEffect(() => {
    if (isOngoing || !applicationDeadline) return undefined;

    const update = () =>
      setRemainingTime(getRemainingTime(applicationDeadline));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [applicationDeadline, isOngoing]);

  const deadlinePassed =
    !!applicationDeadline && new Date(applicationDeadline) < new Date();
  const isClosed = !isOngoing && (!applicationDeadline || deadlinePassed);

  const handleApplyClick = () => {
    if (!isLoggedIn) {
      showLoginModal('jobboard_detail_apply_cta');
      return;
    }
    if (!isClosed && !hasApplied) setIsApplyModalOpen(true);
  };

  // 상태 전환과 토스트는 상세 페이지에서 일괄 처리한다
  const handleSaveClick = () => {
    if (!isLoggedIn) {
      showLoginModal('jobboard_detail_save_cta');
      return;
    }
    onToggleSave?.(!isSaved);
  };

  const showDeadlinePill =
    !isOngoing && !!applicationDeadline && !!remainingTime && !isPillDismissed;

  return (
    <>
      {showDeadlinePill && (
        <div className='pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+80px)] z-[999] flex justify-center px-4 tabletV:hidden'>
          <div className='pointer-events-auto relative px-4 pt-1 pb-2'>
            <div className='flex flex-col items-center justify-center rounded-full border border-green-light03 bg-grayscale-gray00 px-5 py-2.5 text-center shadow-popup-02'>
              <span className='typo-b13 text-green-deep03'>
                {`${t('Apply by', '마감')} ${formatDateToShortDotV2(
                  applicationDeadline,
                )}`}
              </span>
              <span className='typo-b16Strong text-green-deep03'>
                {remainingTime}
              </span>
            </div>

            <button
              type='button'
              aria-label={t('Dismiss', '닫기')}
              onClick={() => setIsPillDismissed(true)}
              className='absolute -right-7 top-0 flex size-6 items-center justify-center text-icon-tertiary'
            >
              <CancelIcon className='size-4' />
            </button>
          </div>
        </div>
      )}

      <div className='fixed inset-x-0 bottom-0 z-[999] flex items-center gap-2 tabletH:border-t tabletH:border-grayscale-gray02 bg-grayscale-gray01 tabletH:bg-grayscale-gray00 p-4 pb-[calc(env(safe-area-inset-bottom)+16px)] tabletV:hidden'>
        <button
          type='button'
          aria-label={isSaved ? t('Saved', '저장됨') : t('Save', '저장')}
          onClick={handleSaveClick}
          disabled={isHidden}
          className='flex px-2 h-full shrink-0 items-center justify-center rounded-full text-icon-primary disabled:text-icon-disable'
        >
          {isSaved ? <BookmarkFilledIcon /> : <BookmarkOutlineIcon />}
        </button>

        <BaseButton
          size='l'
          hideIcon
          width='100%'
          onClick={handleApplyClick}
          isDisabled={hasApplied || isClosed}
        >
          {hasApplied ? t('Applied', '지원 완료') : t('Apply now', '지원하기')}
        </BaseButton>
      </div>

      {isApplyModalOpen && (
        <ApplyNowModal
          job={job}
          isOpen={isApplyModalOpen}
          onClose={() => setIsApplyModalOpen(false)}
          onSuccess={onApplied}
        />
      )}
    </>
  );
};

export default MobileApplyBar;
